import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Spinner from '@/components/shared/Spinner';
import { subscribeUploadStarted } from '@/lib/uploadActivityNotice';
import { cn, formatNumber } from '@/lib/utils';

const FADE_MS = 500;
const HOLD_MS = 3000;

type Phase = 'enter' | 'shown' | 'leave';

type Callout = {
  id: number;
  fileCount: number;
  phase: Phase;
};

type AnchorPosition = {
  top: number;
  right: number;
};

function calloutTitle(fileCount: number) {
  if (fileCount === 1) return 'Uploading 1 file';
  if (fileCount > 1) return `Uploading ${formatNumber(fileCount)} files`;
  return 'Uploading files';
}

function measureAnchor(anchor: HTMLElement): AnchorPosition {
  const rect = anchor.getBoundingClientRect();
  return {
    top: Math.round(rect.bottom + 10),
    right: Math.round(window.innerWidth - rect.right),
  };
}

/**
 * A short-lived confirmation anchored to the upload manager.
 * It fades in when a batch is accepted, holds, then fades out.
 */
export default function UploadActivityCallout({
  anchorRef,
  suppressed,
}: {
  anchorRef: RefObject<HTMLButtonElement | null>;
  suppressed: boolean;
}) {
  const [callout, setCallout] = useState<Callout | null>(null);
  const [position, setPosition] = useState<AnchorPosition | null>(null);
  const calloutRef = useRef<Callout | null>(null);
  const suppressedRef = useRef(suppressed);
  const hideTimerRef = useRef<number | null>(null);
  const leaveTimerRef = useRef<number | null>(null);
  const enterFrameOuterRef = useRef<number | null>(null);
  const enterFrameInnerRef = useRef<number | null>(null);
  const nextIdRef = useRef(0);
  suppressedRef.current = suppressed;

  const syncCallout = useCallback((next: Callout | null) => {
    calloutRef.current = next;
    setCallout(next);
  }, []);

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current !== null) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const clearLeaveTimer = useCallback(() => {
    if (leaveTimerRef.current !== null) {
      window.clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
  }, []);

  const cancelEnterFrames = useCallback(() => {
    if (enterFrameOuterRef.current !== null) {
      window.cancelAnimationFrame(enterFrameOuterRef.current);
      enterFrameOuterRef.current = null;
    }
    if (enterFrameInnerRef.current !== null) {
      window.cancelAnimationFrame(enterFrameInnerRef.current);
      enterFrameInnerRef.current = null;
    }
  }, []);

  const beginLeave = useCallback(() => {
    clearHideTimer();
    const current = calloutRef.current;
    if (!current || current.phase === 'leave') return;

    syncCallout({ ...current, phase: 'leave' });
    clearLeaveTimer();
    leaveTimerRef.current = window.setTimeout(() => {
      leaveTimerRef.current = null;
      if (calloutRef.current?.phase === 'leave') {
        syncCallout(null);
      }
    }, FADE_MS);
  }, [clearHideTimer, clearLeaveTimer, syncCallout]);

  const armHide = useCallback((delay: number) => {
    clearHideTimer();
    hideTimerRef.current = window.setTimeout(() => {
      hideTimerRef.current = null;
      beginLeave();
    }, delay);
  }, [beginLeave, clearHideTimer]);

  useEffect(() => {
    return subscribeUploadStarted(({ fileCount }) => {
      if (suppressedRef.current) return;
      const anchor = anchorRef.current;
      if (!anchor) return;

      clearLeaveTimer();
      setPosition(measureAnchor(anchor));

      const existing = calloutRef.current;
      if (existing && existing.phase !== 'leave') {
        syncCallout({ ...existing, fileCount });
        armHide(HOLD_MS);
        return;
      }

      cancelEnterFrames();
      const id = nextIdRef.current + 1;
      nextIdRef.current = id;
      const entering: Callout = { id, fileCount, phase: 'enter' };
      syncCallout(entering);

      enterFrameOuterRef.current = window.requestAnimationFrame(() => {
        enterFrameOuterRef.current = null;
        enterFrameInnerRef.current = window.requestAnimationFrame(() => {
          enterFrameInnerRef.current = null;
          const current = calloutRef.current;
          if (!current || current.id !== id || current.phase === 'leave') return;
          syncCallout({ ...current, phase: 'shown' });
          armHide(FADE_MS + HOLD_MS);
        });
      });
    });
  }, [anchorRef, armHide, cancelEnterFrames, clearLeaveTimer, syncCallout]);

  useEffect(() => {
    if (suppressed) beginLeave();
  }, [beginLeave, suppressed]);

  useEffect(() => {
    if (!callout) return;

    let frame = 0;
    const tick = () => {
      const anchor = anchorRef.current;
      if (anchor) {
        const next = measureAnchor(anchor);
        setPosition((prev) => (
          prev && prev.top === next.top && prev.right === next.right ? prev : next
        ));
      }
      frame = window.requestAnimationFrame(tick);
    };

    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [callout, anchorRef]);

  useEffect(() => {
    return () => {
      cancelEnterFrames();
      clearHideTimer();
      clearLeaveTimer();
      calloutRef.current = null;
    };
  }, [cancelEnterFrames, clearHideTimer, clearLeaveTimer]);

  if (!callout || !position || typeof document === 'undefined') return null;

  const visible = callout.phase === 'shown';

  return createPortal(
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="pointer-events-none fixed z-[100001]"
      style={{ top: position.top, right: position.right }}
    >
      <div
        className={cn(
          'relative rounded-lg border border-slate-200/90 bg-white',
          'shadow-[0_10px_28px_-12px_rgba(15,23,42,0.28),0_2px_8px_-4px_rgba(15,23,42,0.08)]',
          'transition-opacity duration-500 ease-in-out motion-reduce:transition-none',
          visible ? 'opacity-100' : 'opacity-0',
        )}
      >
        <span
          aria-hidden
          className="absolute -top-[5px] right-[13px] h-2.5 w-2.5 rotate-45 border-l border-t border-slate-200/90 bg-white"
        />
        <div className="flex items-center gap-3 py-2 pl-3.5 pr-6">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-50">
            <Spinner size="sm" className="border-blue-500 border-r-transparent" />
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-medium leading-4 text-slate-900">
              {calloutTitle(callout.fileCount)}
            </span>
            <span className="mt-0.5 block text-xs leading-4 text-slate-500">
              Track progress here
            </span>
          </span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
